import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, FileText, CheckSquare, CalendarPlus, Clock, MapPin, Users, Trash2, Edit, X, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [deadlines, setDeadlines] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [clientFilter, setClientFilter] = useState('Todos');
  const [responsableFilter, setResponsableFilter] = useState('Todos');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deadlinesRes, tasksRes, eventsRes, clientsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/deadlines`),
        axios.get(`${BACKEND_URL}/api/tasks`),
        axios.get(`${BACKEND_URL}/api/events`),
        axios.get(`${BACKEND_URL}/api/clients`),
        axios.get(`${BACKEND_URL}/api/users`),
      ]);
      setDeadlines(deadlinesRes.data);
      setTasks(tasksRes.data);
      setEvents(eventsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(day);
    return days;
  };

  const getDateStr = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = getDateStr(day);

    const dayDeadlines = deadlines
      .filter((d) => d.fecha_vencimiento === dateStr)
      .filter((d) => {
        if (statusFilter === 'Pendientes' && d.estado === 'Realizado') return false;
        if (statusFilter === 'Realizados' && d.estado !== 'Realizado') return false;
        if (clientFilter !== 'Todos' && d.client_id !== clientFilter) return false;
        if (responsableFilter !== 'Todos' && d.responsable_id !== responsableFilter) return false;
        return true;
      })
      .map((d) => ({ ...d, type: 'deadline', label: d.client_name || 'Cliente', sublabel: d.tipo_display || d.tipo_impuesto }));

    const dayTasks = tasks
      .filter((t) => t.fecha_limite === dateStr)
      .filter((t) => {
        if (statusFilter === 'Pendientes' && t.estado === 'Finalizada') return false;
        if (statusFilter === 'Realizados' && t.estado !== 'Finalizada') return false;
        if (clientFilter !== 'Todos' && t.client_id !== clientFilter) return false;
        if (responsableFilter !== 'Todos' && t.responsable_id !== responsableFilter) return false;
        return true;
      })
      .map((t) => ({ ...t, type: 'task', label: t.titulo, sublabel: t.client_name || 'Sin cliente' }));

    const dayEvents = events
      .filter((e) => e.fecha === dateStr)
      .map((e) => ({
        ...e,
        type: 'event',
        label: e.titulo,
        sublabel: e.hora_inicio ? `${e.hora_inicio}${e.hora_fin ? ' - ' + e.hora_fin : ''}` : e.tipo,
      }));

    const all = [...dayEvents, ...dayDeadlines, ...dayTasks];
    // Sort: Pendientes/Vencidos first, Realizados/Finalizados last
    const statusOrder = { 'Vencido': 0, 'Pendiente': 1, 'En proceso': 2, 'En Proceso': 2, 'Realizado': 3, 'Finalizada': 3 };
    all.sort((a, b) => (statusOrder[a.estado] ?? 1) - (statusOrder[b.estado] ?? 1));
    return all;
  };

  const getEventColor = (event) => {
    if (event.type === 'event') {
      const eventColors = {
        Programado: 'bg-violet-500',
        Completado: 'bg-slate-400',
        Cancelado: 'bg-slate-300',
      };
      return eventColors[event.estado] || 'bg-violet-500';
    }
    if (event.type === 'task') {
      return { Pendiente: 'bg-amber-500', 'En proceso': 'bg-orange-500', Finalizada: 'bg-teal-500' }[event.estado] || 'bg-amber-500';
    }
    return { Pendiente: 'bg-indigo-500', 'En proceso': 'bg-blue-500', Realizado: 'bg-emerald-500', Vencido: 'bg-red-500' }[event.estado] || 'bg-slate-500';
  };

  const getTypeIcon = (type) => {
    if (type === 'event') return <CalendarPlus className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />;
    if (type === 'task') return <CheckSquare className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />;
    return <FileText className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />;
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const allEvents = getEventsForDay(day);
    setSelectedDay(day);
    setSelectedDayEvents(allEvents);
    setShowDayModal(true);
  };

  const handleCreateFromDay = () => {
    setShowDayModal(false);
    setEditingEvent(null);
    setShowCreateModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setShowDayModal(false);
    setShowEditModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/events/${eventId}`);
      loadData();
      setSelectedDayEvents(prev => prev.filter(e => e._id !== eventId));
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleMarkDeadline = async (deadlineId, action) => {
    try {
      const estado = action === 'finalizar' ? 'Realizado' : 'Aplazado';
      const res = await axios.patch(`${BACKEND_URL}/api/deadlines/${deadlineId}/status`, { estado });
      if (action === 'aplazar') {
        const newDate = res.data?.update?.fecha_vencimiento;
        alert(`Vencimiento aplazado al ${newDate || 'próximo mes'}`);
      }
      await loadData();
      // Refresh modal content
      const updatedEvents = getEventsForDay(selectedDay);
      setSelectedDayEvents(updatedEvents);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleMarkTask = async (taskId, estado) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/status`, { estado });
      await loadData();
      const updatedEvents = getEventsForDay(selectedDay);
      setSelectedDayEvents(updatedEvents);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const previousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-600">Cargando calendario...</div></div>;
  }

  const monthDays = getMonthDays();

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Calendario</h1>
          <p className="text-sm text-slate-700 leading-relaxed mt-2">Vencimientos, tareas, citas y reuniones</p>
        </div>
        <Button
          onClick={() => { setSelectedDay(new Date().getDate()); setEditingEvent(null); setShowCreateModal(true); }}
          className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
          data-testid="create-event-button"
        >
          <CalendarPlus className="w-4 h-4 mr-2" />
          Agendar Evento
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={previousMonth} data-testid="prev-month-button"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={nextMonth} data-testid="next-month-button"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="calendar-status-filter">
          <div className="flex gap-2">
            {['Todos', 'Pendientes', 'Realizados'].map(tab => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                data-testid={`cal-filter-${tab.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  statusFilter === tab
                    ? tab === 'Realizados' ? 'bg-emerald-600 text-white shadow-sm'
                      : tab === 'Pendientes' ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-[#1F3864] text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1.5 text-xs" data-testid="cal-client-filter">
            <option value="Todos">Todos los clientes</option>
            {clients.map(c => <option key={c._id} value={c._id}>{c.razon_social || c.nit}</option>)}
          </select>
          <select value={responsableFilter} onChange={(e) => setResponsableFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1.5 text-xs" data-testid="cal-responsable-filter">
            <option value="Todos">Todos los responsables</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-semibold tracking-wider uppercase text-slate-500 py-2">{day}</div>
          ))}

          {monthDays.map((day, index) => {
            const dayEvents = day ? getEventsForDay(day) : [];
            const isToday = day && currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear() && day === new Date().getDate();

            return (
              <div
                key={`cell-${index}`}
                onClick={() => handleDayClick(day)}
                className={`min-h-[100px] border border-slate-200 rounded-lg p-2 transition-colors ${
                  day ? 'bg-white hover:bg-slate-50 cursor-pointer' : 'bg-slate-50'
                } ${isToday ? 'ring-2 ring-[#1F3864]' : ''}`}
              >
                {day && (
                  <>
                    <div className="text-sm font-medium text-slate-900 mb-1">{day}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event, idx) => (
                        <div
                          key={`${event.type}-${event._id}-${idx}`}
                          className={`text-xs p-1 rounded text-white ${getEventColor(event)} flex items-start gap-1`}
                          title={`[${event.type === 'event' ? 'Evento' : event.type === 'task' ? 'Tarea' : 'Vencimiento'}] ${event.label}`}
                        >
                          {getTypeIcon(event.type)}
                          <div className="min-w-0">
                            <div className="truncate font-medium">{event.label}</div>
                            <div className="truncate text-[10px] opacity-90">{event.sublabel}</div>
                          </div>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-slate-500 text-center">+{dayEvents.length - 3} más</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Leyenda</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Vencimientos
            </p>
            <div className="flex flex-wrap gap-3">
              {[['bg-indigo-500', 'Pendiente'], ['bg-blue-500', 'En proceso'], ['bg-emerald-500', 'Realizado'], ['bg-red-500', 'Vencido'], ['bg-orange-400', 'Aplazado']].map(([bg, label]) => (
                <div key={label} className="flex items-center gap-2"><div className={`w-3 h-3 ${bg} rounded`}></div><span className="text-sm text-slate-700">{label}</span></div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <CheckSquare className="w-3.5 h-3.5" /> Tareas
            </p>
            <div className="flex flex-wrap gap-3">
              {[['bg-amber-500', 'Pendiente'], ['bg-orange-500', 'En proceso'], ['bg-teal-500', 'Finalizada']].map(([bg, label]) => (
                <div key={label} className="flex items-center gap-2"><div className={`w-3 h-3 ${bg} rounded`}></div><span className="text-sm text-slate-700">{label}</span></div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <CalendarPlus className="w-3.5 h-3.5" /> Citas / Reuniones
            </p>
            <div className="flex flex-wrap gap-3">
              {[['bg-violet-500', 'Programado'], ['bg-slate-400', 'Completado'], ['bg-slate-300', 'Cancelado']].map(([bg, label]) => (
                <div key={label} className="flex items-center gap-2"><div className={`w-3 h-3 ${bg} rounded`}></div><span className="text-sm text-slate-700">{label}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Day Detail Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && `${selectedDay} de ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {selectedDayEvents.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay eventos este día</p>
            ) : (
              selectedDayEvents.map((event, idx) => (
                <div key={`${event.type}-${event._id}-${idx}`} className={`p-3 rounded-lg border ${
                  event.type === 'event' ? 'border-violet-200 bg-violet-50' :
                  event.type === 'task' ? 'border-amber-200 bg-amber-50' :
                  'border-indigo-200 bg-indigo-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 flex-1">
                      {getTypeIcon(event.type)}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{event.label}</p>
                        <p className="text-xs text-slate-600">{event.sublabel}</p>
                        {event.type === 'deadline' && event.fecha_sancion && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Sanción: {event.fecha_sancion}</p>
                        )}
                        {event.type === 'deadline' && event.aplazamientos > 0 && (
                          <p className="text-[10px] text-orange-500 mt-0.5">Aplazado {event.aplazamientos} vez(es)</p>
                        )}
                        {event.type === 'event' && event.ubicacion && (
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{event.ubicacion}</p>
                        )}
                        {event.type === 'event' && event.participant_names?.length > 0 && (
                          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" />{event.participant_names.join(', ')}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${getEventColor(event)}`}>
                        {event.estado}
                      </span>
                      {/* Action buttons for deadlines */}
                      {event.type === 'deadline' && event.estado !== 'Realizado' && (
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            onClick={() => handleMarkDeadline(event._id, 'finalizar')}
                            className="flex items-center gap-0.5 px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-[10px] font-medium transition-colors"
                            title="Marcar como Realizado"
                            data-testid={`finalize-deadline-${event._id}`}
                          >
                            <Check className="w-3 h-3" /> Finalizado
                          </button>
                          <button
                            onClick={() => handleMarkDeadline(event._id, 'aplazar')}
                            className="flex items-center gap-0.5 px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-[10px] font-medium transition-colors"
                            title="Aplazar al próximo mes"
                            data-testid={`postpone-deadline-${event._id}`}
                          >
                            <ArrowRight className="w-3 h-3" /> Aplazado
                          </button>
                        </div>
                      )}
                      {/* Action buttons for tasks */}
                      {event.type === 'task' && event.estado !== 'Finalizada' && (
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            onClick={() => handleMarkTask(event._id, 'Finalizada')}
                            className="flex items-center gap-0.5 px-2 py-1 bg-teal-500 hover:bg-teal-600 text-white rounded text-[10px] font-medium transition-colors"
                            title="Marcar como Finalizada"
                            data-testid={`finalize-task-${event._id}`}
                          >
                            <Check className="w-3 h-3" /> Finalizada
                          </button>
                        </div>
                      )}
                      {/* Edit/Delete for events */}
                      {event.type === 'event' && (
                        <div className="flex items-center gap-1 mt-1">
                          <button onClick={() => handleEditEvent(event)} className="p-1 hover:bg-slate-200 rounded" data-testid={`edit-event-${event._id}`}>
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </button>
                          <button onClick={() => handleDeleteEvent(event._id)} className="p-1 hover:bg-red-100 rounded" data-testid={`delete-event-${event._id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end pt-2 border-t">
            <Button onClick={handleCreateFromDay} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="create-event-from-day">
              <CalendarPlus className="w-4 h-4 mr-2" />
              Agendar Evento
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agendar Evento</DialogTitle>
          </DialogHeader>
          <EventForm
            initialDate={selectedDay ? getDateStr(selectedDay) : ''}
            clients={clients}
            users={users}
            onSuccess={() => { setShowCreateModal(false); loadData(); }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Evento</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <EventForm
              event={editingEvent}
              initialDate={editingEvent.fecha}
              clients={clients}
              users={users}
              onSuccess={() => { setShowEditModal(false); loadData(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Event Form Component
const EventForm = ({ event, initialDate, clients, users, onSuccess }) => {
  const [formData, setFormData] = useState({
    titulo: event?.titulo || '',
    descripcion: event?.descripcion || '',
    tipo: event?.tipo || 'Reunión',
    fecha: event?.fecha || initialDate || '',
    hora_inicio: event?.hora_inicio || '',
    hora_fin: event?.hora_fin || '',
    cliente_id: event?.cliente_id || '',
    participantes: event?.participantes || [],
    ubicacion: event?.ubicacion || '',
    estado: event?.estado || 'Programado',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleParticipant = (userId) => {
    setFormData(prev => ({
      ...prev,
      participantes: prev.participantes.includes(userId)
        ? prev.participantes.filter(id => id !== userId)
        : [...prev.participantes, userId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (event?._id) {
        await axios.put(`${BACKEND_URL}/api/events/${event._id}`, formData);
      } else {
        await axios.post(`${BACKEND_URL}/api/events`, formData);
      }
      onSuccess();
    } catch (error) {
      alert('Error al guardar el evento');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Título *</Label>
          <Input name="titulo" value={formData.titulo} onChange={handleChange} required placeholder="Ej: Reunión con cliente" data-testid="event-titulo" />
        </div>
        <div>
          <Label>Tipo</Label>
          <select name="tipo" value={formData.tipo} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="event-tipo">
            <option value="Reunión">Reunión</option>
            <option value="Cita">Cita</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
        <div>
          <Label>Estado</Label>
          <select name="estado" value={formData.estado} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="event-estado">
            <option value="Programado">Programado</option>
            <option value="Completado">Completado</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
        <div>
          <Label>Fecha *</Label>
          <Input type="date" name="fecha" value={formData.fecha} onChange={handleChange} required data-testid="event-fecha" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Hora Inicio</Label>
            <Input type="time" name="hora_inicio" value={formData.hora_inicio} onChange={handleChange} data-testid="event-hora-inicio" />
          </div>
          <div>
            <Label>Hora Fin</Label>
            <Input type="time" name="hora_fin" value={formData.hora_fin} onChange={handleChange} data-testid="event-hora-fin" />
          </div>
        </div>
        <div>
          <Label>Cliente (opcional)</Label>
          <select name="cliente_id" value={formData.cliente_id} onChange={handleChange} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="event-cliente">
            <option value="">Sin cliente</option>
            {clients.map(c => (
              <option key={c._id} value={c._id}>
                {c.razon_social || `${c.primer_nombre || ''} ${c.primer_apellido || ''}`.trim()}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Ubicación</Label>
          <Input name="ubicacion" value={formData.ubicacion} onChange={handleChange} placeholder="Ej: Oficina, Virtual" data-testid="event-ubicacion" />
        </div>
        <div className="col-span-2">
          <Label>Participantes</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {users.map(u => (
              <button
                key={u._id}
                type="button"
                onClick={() => toggleParticipant(u._id)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  formData.participantes.includes(u._id)
                    ? 'bg-[#1F3864] text-white border-[#1F3864]'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-[#1F3864]'
                }`}
                data-testid={`participant-${u._id}`}
              >
                {u.nombre || u.name}
              </button>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <Label>Descripción</Label>
          <textarea
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
            rows={2}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Detalles del evento..."
            data-testid="event-descripcion"
          />
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={submitting} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="event-submit">
          {submitting ? 'Guardando...' : event?._id ? 'Actualizar Evento' : 'Crear Evento'}
        </Button>
      </div>
    </form>
  );
};

export default Calendar;
